import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Row, Col, Card, Table, Button, Alert, Spinner, Badge,
  Modal, Form
} from 'react-bootstrap';
import { qscAPI } from '../utils/api';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
const imgUrl = (filePath) => `${API_BASE}/${filePath.replace(/^\//, '')}`;

const improvStatusBadge = (status) => {
  if (status === 'approved') return <Badge bg="success">อนุมัติแล้ว</Badge>;
  if (status === 'rejected') return <Badge bg="danger">ถูกปฏิเสธ</Badge>;
  return <Badge bg="warning" text="dark">รอการตรวจสอบ</Badge>;
};

const QscImprovementReview = () => {
  const [improvements, setImprovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [branchFilter, setBranchFilter] = useState('');
  const hasLoadedRef = useRef(false);

  // Review modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedImp, setSelectedImp] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadImprovements();
    }
  }, []);

  const loadImprovements = async () => {
    try {
      setLoading(true);
      const res = await qscAPI.getImprovements();
      setImprovements(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const openReview = (imp) => {
    setSelectedImp(imp);
    setReviewComment('');
    setReviewError('');
    setShowModal(true);
  };

  const handleReview = async (status) => {
    setSubmitting(true);
    setReviewError('');
    try {
      await qscAPI.reviewImprovement(selectedImp.id, { status, reviewerComment: reviewComment });
      setShowModal(false);
      await loadImprovements();
    } catch (err) {
      setReviewError(err.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setSubmitting(false);
    }
  };

  const branchNames = [...new Set(improvements.map(i => i.submitter?.fullName || i.submitter?.username).filter(Boolean))].sort();

  const filtered = improvements.filter(i => {
    const statusMatch = filter === 'all' || i.status === filter;
    const branchMatch = !branchFilter || (i.submitter?.fullName || i.submitter?.username) === branchFilter;
    return statusMatch && branchMatch;
  });

  return (
    <Container fluid className="main-content">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2>
            <i className="bi bi-images me-2"></i>
            รีวิวการแก้ไขจากสาขา
          </h2>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/qsc" variant="outline-secondary" size="sm">
            <i className="bi bi-arrow-left me-1"></i>
            กลับ
          </Button>
        </Col>
      </Row>

      {/* Filter tabs */}
      <div className="mb-3 d-flex gap-2">
        {[
          { key: 'pending', label: 'รอตรวจสอบ', bg: 'warning' },
          { key: 'approved', label: 'อนุมัติแล้ว', bg: 'success' },
          { key: 'rejected', label: 'ถูกปฏิเสธ', bg: 'danger' },
          { key: 'all', label: 'ทั้งหมด', bg: 'secondary' }
        ].map(({ key, label, bg }) => (
          <Button
            key={key}
            variant={filter === key ? bg : `outline-${bg}`}
            size="sm"
            onClick={() => setFilter(key)}
            className={bg === 'warning' && filter !== key ? 'text-dark' : ''}
          >
            {label}
            {key !== 'all' && (
              <Badge bg="light" text="dark" className="ms-1">
                {improvements.filter(i => i.status === key).length}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Branch filter */}
      <div className="mb-3">
        <Form.Select
          size="sm"
          style={{ maxWidth: 250 }}
          value={branchFilter}
          onChange={e => setBranchFilter(e.target.value)}
        >
          <option value="">ทุกสาขา</option>
          {branchNames.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </Form.Select>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Card>
          <Card.Body className="p-0">
            {filtered.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                ไม่มีรายการในหมวดนี้
              </div>
            ) : (
              <Table hover responsive className="mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>สาขา</th>
                    <th>วันที่ตรวจ</th>
                    <th>รหัส</th>
                    <th>รายการ</th>
                    <th>รายละเอียดการแก้ไข</th>
                    <th className="text-center">รูปภาพ</th>
                    <th className="text-center">สถานะ</th>
                    <th className="text-center">ดำเนินการ</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(imp => (
                    <tr key={imp.id}>
                      <td>
                        {imp.submitter?.fullName || imp.submitter?.username || '-'}
                      </td>
                      <td>{imp.assessment?.visitDate || '-'}</td>
                      <td><code>{imp.item?.itemCode}</code></td>
                      <td style={{ fontSize: '0.875rem', maxWidth: 200 }}>{imp.item?.description}</td>
                      <td style={{ fontSize: '0.875rem', maxWidth: 200 }}>{imp.description || '-'}</td>
                      <td className="text-center">
                        <div className="d-flex flex-wrap gap-1 justify-content-center">
                          {imp.photos?.length > 0 ? imp.photos.map(photo => (
                            <a
                              key={photo.id}
                              href={`${imgUrl(photo.filePath)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={`${imgUrl(photo.filePath)}`}
                                alt="improvement"
                                style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                              />
                            </a>
                          )) : (
                            <span className="text-muted small">ไม่มีรูป</span>
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        {improvStatusBadge(imp.status)}
                        {imp.reviewerComment && (
                          <div className="text-muted small mt-1">{imp.reviewerComment}</div>
                        )}
                      </td>
                      <td className="text-center">
                        {imp.status === 'pending' ? (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => openReview(imp)}
                          >
                            <i className="bi bi-check2-circle me-1"></i>
                            ตรวจสอบ
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => openReview(imp)}
                          >
                            แก้ไข
                          </Button>
                        )}
                        {' '}
                        <Button
                          as={Link}
                          to={`/qsc/${imp.assessment?.id}`}
                          size="sm"
                          variant="outline-info"
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Review modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-check2-circle me-2"></i>
            ตรวจสอบการแก้ไข: <code>{selectedImp?.item?.itemCode}</code>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reviewError && <Alert variant="danger">{reviewError}</Alert>}

          <div className="mb-3 small text-muted">{selectedImp?.item?.description}</div>

          {/* Before / After image comparison */}
          <Row className="mb-3 g-3">
            <Col xs={6}>
              <div className="text-center fw-semibold small mb-2 text-muted">ก่อนแก้ (หลักฐาน HQ)</div>
              {selectedImp?.evidencePhotos?.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  {selectedImp.evidencePhotos.map(photo => (
                    <a key={photo.id} href={`${imgUrl(photo.filePath)}`} target="_blank" rel="noopener noreferrer">
                      <img src={`${imgUrl(photo.filePath)}`} alt="evidence"
                        style={{ width: '100%', maxWidth: 220, height: 180, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center rounded text-muted small"
                  style={{ height: 180, background: '#f0faf0', border: '1px dashed #ccc' }}>
                  ไม่มีรูปหลักฐาน
                </div>
              )}
            </Col>
            <Col xs={6}>
              <div className="text-center fw-semibold small mb-2 text-muted">หลังแก้ (ส่งโดยสาขา)</div>
              {selectedImp?.photos?.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  {selectedImp.photos.map(photo => (
                    <a key={photo.id} href={`${imgUrl(photo.filePath)}`} target="_blank" rel="noopener noreferrer">
                      <img src={`${imgUrl(photo.filePath)}`} alt="improvement"
                        style={{ width: '100%', maxWidth: 220, height: 180, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center rounded text-muted small"
                  style={{ height: 180, background: '#f0faf0', border: '1px dashed #ccc' }}>
                  ไม่มีรูปการแก้ไข
                </div>
              )}
            </Col>
          </Row>

          <div className="mb-3 small">
            <strong>รายละเอียดการแก้ไข:</strong> {selectedImp?.description || '-'}
          </div>

          <Form.Group>
            <Form.Label>ความเห็น (ไม่บังคับ)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={reviewComment}
              onChange={e => setReviewComment(e.target.value)}
              placeholder="ความเห็นเพิ่มเติม..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
            ยกเลิก
          </Button>
          <Button variant="danger" onClick={() => handleReview('rejected')} disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : <><i className="bi bi-x-circle me-1"></i>ปฏิเสธ</>}
          </Button>
          <Button variant="success" onClick={() => handleReview('approved')} disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : <><i className="bi bi-check-circle me-1"></i>อนุมัติ</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default QscImprovementReview;
